import { useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { celebrate } from "@/lib/celebrate";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import RunSOPButton from "@/components/sops/RunSOPButton";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, Plus, Trash2, ArrowRight, Receipt, Wallet, BarChart3, Boxes, Search, Printer, FileSpreadsheet, FileImage, Send, Eye, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import { CashfreeConfigButton, InvoicePaySection } from "@/components/CashfreePanel";
import EmailsTab from "@/components/EmailsTab";
import AdvancedTable from "@/components/table/AdvancedTable";

const TABS = [
  { key: "quotations", label: "Quotations", icon: FileText },
  { key: "sales-orders", label: "Sales orders", icon: ArrowRight },
  { key: "invoices", label: "Invoices", icon: Receipt },
  { key: "payments", label: "Payments", icon: Wallet },
  { key: "items", label: "Item catalog", icon: Boxes },
  { key: "reports", label: "Reports", icon: BarChart3 },
];

export default function Sales() {
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("tab") || "quotations";
  });
  const setTabAndUrl = (k) => {
    setTab(k);
    const url = new URL(window.location);
    url.searchParams.set("tab", k);
    window.history.replaceState({}, "", url);
  };

  return (
    <>
      <PageHeader
        eyebrow="Revenue"
        title="Sales & invoices"
        density="dense"
        description="Quotations → sales orders → invoices → payments. Reports built in."
        testid="sales-header"
      />
      <div className="px-4 sm:px-6 md:px-10 mb-3">
        <div className="border-b border-border flex flex-wrap gap-0" data-testid="sales-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTabAndUrl(t.key)}
                className={`px-4 py-2.5 text-sm inline-flex items-center gap-2 border-b-2 ${active ? "border-primary font-bold text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                data-testid={`sales-tab-${t.key}`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="px-4 sm:px-6 md:px-10 pb-10">
        {tab === "invoices" && <div className="flex justify-end mb-3"><CashfreeConfigButton /></div>}
        {tab === "items" && <ItemsTab />}
        {tab === "quotations" && <DocsTab kind="quotations" />}
        {tab === "sales-orders" && <DocsTab kind="sales-orders" />}
        {tab === "invoices" && <DocsTab kind="invoices" />}
        {tab === "payments" && <PaymentsTab />}
        {tab === "reports" && <ReportsTab />}
      </div>
    </>
  );
}

/* ──────────────────────── ITEM CATALOG ─────────────────────── */

function ItemsTab() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/items?active_only=false&q=${encodeURIComponent(q)}`);
      setRows(data || []);
    } finally { setBusy(false); }
  }, [activeWorkspaceId, q]);
  useEffect(() => { load(); }, [load]);

  const del = async (it) => {
    if (!confirm(`Delete item "${it.name}"?`)) return;
    try { await api.delete(`/items/${it.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="border border-border bg-muted/40 p-3 flex items-center gap-2 flex-1" data-testid="items-filters">
          <Search className="w-3.5 h-3.5" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SKU, name, category…" className="h-8 w-80 text-xs" data-testid="items-q" />
          <span className="text-[11px] ml-auto">{rows.length} items</span>
        </div>
        <Button onClick={() => setEditing({})} className="rounded-none" data-testid="items-new-btn">
          <Plus className="w-4 h-4 mr-2" /> New item
        </Button>
      </div>
      {busy && rows.length === 0 ? <div className="text-sm">Loading…</div>
        : rows.length === 0 ? (
          <EmptyState title="No items yet" description="Add products and services you sell — pricing, tax, units, HSN/SAC codes." icon={Boxes} testid="items-empty"
            action={<Button className="rounded-none" onClick={() => setEditing({})} data-testid="items-empty-cta">Add first item</Button>} />
        ) : (
          <div className="border border-border" data-testid="items-table">
            <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
              <div className="col-span-2">SKU</div>
              <div className="col-span-3">Name</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-1">Unit</div>
              <div className="col-span-1 text-right">Rate</div>
              <div className="col-span-1 text-right">Tax %</div>
              <div className="col-span-1">Active</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {rows.map((it) => (
              <div key={it.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border items-center text-sm" data-testid={`item-row-${it.id}`}>
                <button onClick={() => setEditing(it)} className="col-span-2 text-left font-mono text-xs">{it.sku || "—"}</button>
                <button onClick={() => setEditing(it)} className="col-span-3 text-left min-w-0">
                  <div className="font-semibold truncate">{it.name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{it.description || ""}</div>
                </button>
                <div className="col-span-2 text-xs truncate">{it.category || "—"}</div>
                <div className="col-span-1 text-xs">{it.unit}</div>
                <div className="col-span-1 text-right font-mono text-xs">{it.currency} {Number(it.rate).toLocaleString()}</div>
                <div className="col-span-1 text-right text-xs">{it.tax_pct}%</div>
                <div className="col-span-1"><span className={`chip text-[10px] ${it.active ? "chip-approved" : "chip-rejected"}`}>{it.active ? "yes" : "no"}</span></div>
                <div className="col-span-1 text-right flex items-center gap-1 justify-end">
                  <RunSOPButton linkKind="item_catalog" linkId={it.id} label="" size="sm" variant="ghost" testKey={`item-${it.id}`} />
                  <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => del(it)} data-testid={`item-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      {editing && <ItemDialog item={editing} onClose={() => setEditing(null)} onSaved={async () => { await load(); setEditing(null); }} />}
    </>
  );
}

function ItemDialog({ item, onClose, onSaved }) {
  const { activeWorkspaceId } = useAuth();
  const isNew = !item?.id;
  const [form, setForm] = useState({
    sku: item.sku || "", name: item.name || "", description: item.description || "",
    category: item.category || "", unit: item.unit || "unit",
    rate: item.rate ?? 0, currency: item.currency || "INR", tax_pct: item.tax_pct ?? 0,
    hsn_sac: item.hsn_sac || "", is_service: !!item.is_service, active: item.active !== false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, rate: Number(form.rate || 0), tax_pct: Number(form.tax_pct || 0) };
      if (isNew) { await api.post(`/workspaces/${activeWorkspaceId}/items`, payload); toast.success("Item added"); }
      else { await api.patch(`/items/${item.id}`, payload); toast.success("Item updated"); }
      await onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="item-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add item" : `Edit · ${item.name}`}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="SKU" value={form.sku} onChange={(v) => set("sku", v)} testid="item-sku" />
          <Field label="Name *" value={form.name} onChange={(v) => set("name", v)} testid="item-name" />
          <Field label="Category" value={form.category} onChange={(v) => set("category", v)} testid="item-category" />
          <Field label="Unit" value={form.unit} onChange={(v) => set("unit", v)} testid="item-unit" />
          <Field label="Rate" type="number" value={form.rate} onChange={(v) => set("rate", v)} testid="item-rate" />
          <Field label="Currency" value={form.currency} onChange={(v) => set("currency", v)} testid="item-currency" />
          <Field label="Tax %" type="number" value={form.tax_pct} onChange={(v) => set("tax_pct", v)} testid="item-tax" />
          <Field label="HSN/SAC" value={form.hsn_sac} onChange={(v) => set("hsn_sac", v)} testid="item-hsn" />
          <div className="col-span-2">
            <Label className="block mb-1.5 text-sm font-semibold">Description</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="item-description" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_service} onChange={(e) => set("is_service", e.target.checked)} data-testid="item-is-service" /> Is a service
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="item-active" /> Active (show in catalog)
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !form.name} className="rounded-none" data-testid="item-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────────── QUOTATIONS / SO / INVOICES (shared) ───────────────── */

const DOC_META = {
  "quotations": { singular: "Quotation", prefix: "Q", endpoint: "quotations", routeKey: "quotation", canCreate: true, statuses: ["draft", "sent", "accepted", "rejected", "expired"] },
  "sales-orders": { singular: "Sales order", prefix: "SO", endpoint: "sales-orders", routeKey: "so", canCreate: false, statuses: ["open", "fulfilled", "cancelled"] },
  "invoices": { singular: "Invoice", prefix: "INV", endpoint: "invoices", routeKey: "invoice", canCreate: true, statuses: ["draft", "sent", "partial", "paid", "overdue", "void"] },
};

function DocsTab({ kind }) {
  const { activeWorkspaceId } = useAuth();
  const meta = DOC_META[kind];
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null); // {} for new, doc for edit
  const [viewing, setViewing] = useState(null); // doc for view+print
  const [filterStatus, setFilterStatus] = useState("");
  // Nov 2026 · Bulk invoice actions — selection set + email dialog state.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [bulkEmailForm, setBulkEmailForm] = useState({
    subject: "Invoice {doc_no} from {workspace_name}",
    body: "Hi {client_name},\n\nPlease find attached invoice {doc_no} for {grand_total}, due {due_date}.\n\nThanks",
    cc: "",
  });

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const params = filterStatus ? `?status=${filterStatus}` : "";
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/${meta.endpoint}${params}`);
      setRows(data || []);
    } finally { setBusy(false); }
  }, [activeWorkspaceId, meta.endpoint, filterStatus]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const out = { all: rows.length };
    meta.statuses.forEach((s) => { out[s] = rows.filter((r) => r.status === s).length; });
    return out;
  }, [rows, meta]);

  // ──── Nov 2026 · Bulk invoice actions ────
  const bulkMarkSent = async (sel) => {
    const ids = (sel || []).map((d) => d.id).filter(Boolean);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/invoices/bulk-mark-sent`,
        { invoice_ids: ids });
      toast.success(`Marked ${data.marked_sent} sent${data.already_sent ? ` · ${data.already_sent} already sent` : ""}${data.failed ? ` · ${data.failed} failed` : ""}`);
      setSelectedIds(new Set());
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk mark-sent failed")); }
    finally { setBulkBusy(false); }
  };
  const bulkDownloadPdf = async (sel) => {
    const ids = (sel || []).map((d) => d.id).filter(Boolean);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      // Response is a ZIP blob — stream to a download.
      const resp = await api.post(
        `/workspaces/${activeWorkspaceId}/invoices/bulk-pdf`,
        { invoice_ids: ids },
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a");
      const cd = resp.headers?.["content-disposition"] || "";
      const nm = /filename="?([^"]+)"?/.exec(cd)?.[1] || "invoices.zip";
      a.href = url; a.download = nm;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ids.length} invoice${ids.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk download failed")); }
    finally { setBulkBusy(false); }
  };
  const bulkEmailSubmit = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const payload = {
        invoice_ids: ids,
        subject: bulkEmailForm.subject || null,
        body: (bulkEmailForm.body || "").replace(/\n/g, "<br/>") || null,
        cc: bulkEmailForm.cc
          ? bulkEmailForm.cc.split(/[,;]/).map((s) => s.trim()).filter(Boolean)
          : null,
      };
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/invoices/bulk-email`, payload);
      toast.success(`Emailed ${data.sent}${data.skipped_no_email ? ` · ${data.skipped_no_email} skipped (no email)` : ""}${data.failed ? ` · ${data.failed} failed` : ""}`);
      setBulkEmailOpen(false);
      setSelectedIds(new Set());
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk email failed")); }
    finally { setBulkBusy(false); }
  };

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-3">
        <StatTile label="All" count={counts.all} active={!filterStatus} onClick={() => setFilterStatus("")} testid={`${kind}-stat-all`} />
        {meta.statuses.map((s) => (
          <StatTile key={s} label={s} count={counts[s] || 0} active={filterStatus === s} onClick={() => setFilterStatus(filterStatus === s ? "" : s)} testid={`${kind}-stat-${s}`} />
        ))}
      </div>
      {meta.canCreate && (
        <div className="flex justify-end mb-3">
          <Button onClick={() => setEditing({})} className="rounded-none" data-testid={`${kind}-new-btn`}>
            <Plus className="w-4 h-4 mr-2" /> New {meta.singular.toLowerCase()}
          </Button>
        </div>
      )}
      {(() => {
        const columns = [
          {
            key: "doc_no", label: "Doc no", width: 130, sortable: true, frozen: true, filterType: "text",
            accessor: (d) => d.doc_no,
            render: (d) => (
              <button onClick={() => setViewing(d)} className="text-left font-mono text-xs font-semibold hover:text-primary" data-testid={`${kind}-doc-${d.doc_no}`}>
                {d.doc_no}
              </button>
            ),
          },
          { key: "client_name", label: "Client", width: 240, sortable: true, groupable: true, filterType: "text" },
          {
            // Feb 2026 · Subject surfaces the one-line summary set in
            // the dialog so AR/AM can skim without opening each row.
            key: "subject", label: "Subject", width: 240, sortable: true, filterType: "text",
            accessor: (d) => d.subject || "",
            render: (d) => d.subject
              ? <span className="text-xs truncate inline-block max-w-[220px]" title={d.subject}>{d.subject}</span>
              : <span className="text-xs text-muted-foreground">—</span>,
          },
          {
            key: "issue_date", label: "Issued", width: 110, sortable: true, filterType: "date-range",
            render: (d) => <span className="text-xs">{d.issue_date || "—"}</span>,
          },
          {
            key: "due_date", label: kind === "invoices" ? "Due" : "Valid until", width: 110, sortable: true, filterType: "date-range",
            accessor: (d) => d.due_date || d.valid_until || "",
            render: (d) => <span className="text-xs text-muted-foreground">{d.due_date || d.valid_until || "—"}</span>,
          },
          {
            key: "status", label: "Status", width: 110, sortable: true, groupable: true, filterType: "select",
            filterOptions: meta.statuses.map((s) => ({ value: s, label: s })),
            render: (d) => <StatusChip status={d.status} />,
          },
          {
            key: "grand_total", label: "Total", width: 140, sortable: true, align: "right", type: "currency", sum: true,
            filterType: "number-range",
            accessor: (d) => Number(d.grand_total || 0),
            exportValue: (d) => Number(d.grand_total || 0),
            render: (d) => <span className="font-mono text-sm">{d.currency} {Number(d.grand_total || 0).toLocaleString()}</span>,
          },
          {
            key: "balance_due", label: "Balance", width: 120, sortable: true, align: "right", type: "currency", sum: kind === "invoices",
            accessor: (d) => Number(d.balance_due || 0),
            exportValue: (d) => Number(d.balance_due || 0),
            render: (d) => <span className="font-mono text-xs">{d.balance_due != null ? `${d.currency} ${Number(d.balance_due).toLocaleString()}` : "—"}</span>,
            visible: kind === "invoices",
          },
        ];
        return (
          <AdvancedTable
            tableKey={kind}
            title={meta.singular + "s"}
            rows={rows}
            columns={columns}
            loading={busy && rows.length === 0}
            emptyIcon={FileText}
            emptyTitle={`No ${meta.singular.toLowerCase()}s yet`}
            emptyDescription={meta.canCreate ? `Create your first ${meta.singular.toLowerCase()} to begin tracking deals.` : `${meta.singular}s are generated from quotations.`}
            onRowClick={(d) => setViewing(d)}
            {... (kind === "invoices" ? {
              selectable: true,
              selectedIds,
              onSelectionChange: setSelectedIds,
              renderBulkBar: (sel) => (
                <>
                  <span className="text-[11px] text-muted-foreground font-mono" data-testid="inv-bulk-count">
                    {sel.length} invoice{sel.length === 1 ? "" : "s"}
                  </span>
                  <Button size="sm" variant="outline" className="rounded-none h-7"
                            disabled={bulkBusy}
                            onClick={() => bulkMarkSent(sel)}
                            data-testid="inv-bulk-mark-sent">
                    <Send className="w-3.5 h-3.5 mr-1" />Mark sent
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none h-7"
                            disabled={bulkBusy}
                            onClick={() => bulkDownloadPdf(sel)}
                            data-testid="inv-bulk-pdf">
                    <FileText className="w-3.5 h-3.5 mr-1" />Download ZIP
                  </Button>
                  <Button size="sm" className="rounded-none h-7 bg-blue-600 hover:bg-blue-700 text-white"
                            disabled={bulkBusy}
                            onClick={() => setBulkEmailOpen(true)}
                            data-testid="inv-bulk-email">
                    <Send className="w-3.5 h-3.5 mr-1" />Email PDF
                  </Button>
                </>
              ),
            } : {})}
            renderRowActions={(d) => (
              <span className="flex gap-1 justify-end">
                {kind === "quotations" && (d.status === "sent" || d.status === "accepted") && !d.converted_invoice_id && (
                  <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-emerald-700 hover:text-emerald-900" title="Convert to invoice" data-testid={`quotations-convert-inv-${d.id}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const { data: inv } = await api.post(`/quotations/${d.id}/convert-to-invoice`);
                        toast.success(`Invoice ${inv.doc_no} created from ${d.doc_no}`);
                        await load();
                      } catch (err) {
                        if (err?.response?.status === 409) {
                          const existing = err.response.data?.detail?.existing_invoice_id;
                          if (window.confirm(`Already converted to invoice ${existing}. Convert again anyway?`)) {
                            try {
                              const { data: inv } = await api.post(`/quotations/${d.id}/convert-to-invoice?force=true`);
                              toast.success(`Invoice ${inv.doc_no} created (force)`);
                              await load();
                            } catch (err2) { toast.error(apiErrorMessage(err2, "Force convert failed")); }
                          }
                        } else {
                          toast.error(apiErrorMessage(err, "Convert failed"));
                        }
                      }
                    }}>
                    → Invoice
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={(e) => { e.stopPropagation(); setViewing(d); }} title="View / print" data-testid={`${kind}-view-${d.id}`}>
                  <Printer className="w-3.5 h-3.5" />
                </Button>
                {meta.canCreate && (
                  <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={(e) => { e.stopPropagation(); setEditing(d); }} data-testid={`${kind}-edit-${d.id}`}>
                    Edit
                  </Button>
                )}
              </span>
            )}
          />
        );
      })()}

      {editing && meta.canCreate && (
        <DocEditor kind={kind} meta={meta} doc={editing} onClose={() => setEditing(null)} onSaved={async () => { await load(); setEditing(null); }} />
      )}
      {viewing && (
        <DocView kind={kind} meta={meta} doc={viewing} onClose={() => setViewing(null)} onChanged={load} />
      )}
      {/* Nov 2026 · Bulk-email invoices dialog */}
      <Dialog open={bulkEmailOpen} onOpenChange={setBulkEmailOpen}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="inv-bulk-email-dialog">
          <DialogHeader>
            <DialogTitle>Email {selectedIds.size} invoice{selectedIds.size === 1 ? "" : "s"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-[11px] text-muted-foreground bg-muted/40 p-2">
              Each invoice is sent to its client's <b>billing_email</b> with its PDF attached. Clients missing a billing email are skipped. Placeholders you can use: <code>{"{doc_no} {client_name} {grand_total} {currency} {due_date} {workspace_name}"}</code>
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={bulkEmailForm.subject} onChange={(e) => setBulkEmailForm((s) => ({ ...s, subject: e.target.value }))} data-testid="inv-bulk-email-subject" className="h-10" />
            </div>
            <div>
              <Label>Body</Label>
              <Textarea rows={6} value={bulkEmailForm.body} onChange={(e) => setBulkEmailForm((s) => ({ ...s, body: e.target.value }))} data-testid="inv-bulk-email-body" />
            </div>
            <div>
              <Label>CC (optional, comma-separated)</Label>
              <Input value={bulkEmailForm.cc} onChange={(e) => setBulkEmailForm((s) => ({ ...s, cc: e.target.value }))} data-testid="inv-bulk-email-cc" placeholder="finance@…, cc@…" className="h-10" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkEmailOpen(false)} disabled={bulkBusy}>Cancel</Button>
            <Button onClick={bulkEmailSubmit} disabled={bulkBusy || !bulkEmailForm.subject || !bulkEmailForm.body} data-testid="inv-bulk-email-submit" className="bg-blue-600 hover:bg-blue-700 text-white">
              {bulkBusy ? "Sending…" : `Send to ${selectedIds.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatusChip({ status }) {
  const cls = {
    draft: "chip-internal", sent: "chip-pending", accepted: "chip-approved",
    open: "chip-pending", fulfilled: "chip-approved", cancelled: "chip-rejected",
    paid: "chip-approved", partial: "chip-pending", overdue: "chip-rejected", void: "",
    rejected: "chip-rejected", expired: "chip-rejected",
  }[status] || "";
  return <span className={`chip text-[10px] ${cls}`} data-testid={`status-${status}`}>{status}</span>;
}

function DocEditor({ kind, meta, doc, onClose, onSaved }) {
  const { activeWorkspaceId } = useAuth();
  const isNew = !doc?.id;
  const [clients, setClients] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [form, setForm] = useState({
    client_id: doc.client_id || "",
    issue_date: (doc.issue_date || new Date().toISOString().slice(0, 10)),
    valid_until: doc.valid_until || "",
    due_date: doc.due_date || "",
    currency: doc.currency || "INR",
    items: doc.items?.length ? doc.items : [{ description: "", qty: 1, rate: 0, tax_pct: 18, discount_pct: 0 }],
    discount_pct: doc.discount_pct || 0,
    additional_charges: doc.additional_charges || 0,
    subject: doc.subject || "",
    notes: doc.notes || "",
    terms: doc.terms || "",
  });
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get(`/workspaces/${activeWorkspaceId}/clients`).then(({ data }) => setClients(data || []));
    api.get(`/workspaces/${activeWorkspaceId}/items`).then(({ data }) => setCatalog(data || []));
  }, [activeWorkspaceId]);

  // Smart Composer — load client history when client_id changes.
  useEffect(() => {
    if (!form.client_id || !activeWorkspaceId) { setSuggestions([]); return; }
    api.get(`/workspaces/${activeWorkspaceId}/sales-cc/smart-suggest?client_id=${form.client_id}`)
      .then(({ data }) => setSuggestions(data.suggestions || []))
      .catch(() => setSuggestions([]));
  }, [form.client_id, activeWorkspaceId]);

  const addSuggestion = (s) => {
    setForm((prev) => {
      // If first row is empty, fill it. Else append.
      const items = [...prev.items];
      const target = items[0] && !items[0].description ? 0 : items.length;
      items[target] = {
        description: s.description,
        qty: s.avg_qty || 1,
        rate: s.avg_rate || 0,
        tax_pct: s.tax_pct ?? 18,
        discount_pct: 0,
        unit: s.unit || "unit",
        hsn_sac: s.hsn_sac || null,
      };
      return { ...prev, items };
    });
    toast.success(`Added: ${s.description}`);
  };

  const setItem = (idx, k, v) => {
    setForm((s) => {
      const items = [...s.items];
      items[idx] = { ...items[idx], [k]: v };
      return { ...s, items };
    });
  };
  const addItem = () => setForm((s) => ({ ...s, items: [...s.items, { description: "", qty: 1, rate: 0, tax_pct: 18, discount_pct: 0 }] }));
  const removeItem = (idx) => setForm((s) => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));
  const pickFromCatalog = (idx, itemId) => {
    const it = catalog.find((c) => c.id === itemId);
    if (!it) return;
    setForm((s) => {
      const items = [...s.items];
      items[idx] = { ...items[idx], item_id: it.id, description: it.name + (it.description ? ` · ${it.description}` : ""), rate: it.rate, tax_pct: it.tax_pct, unit: it.unit, hsn_sac: it.hsn_sac };
      return { ...s, items };
    });
  };

  // live totals
  const totals = useMemo(() => {
    let subtotal = 0, lineDisc = 0, taxTotal = 0;
    form.items.forEach((li) => {
      const lineSub = (Number(li.qty || 0)) * (Number(li.rate || 0));
      const lineD = lineSub * (Number(li.discount_pct || 0) / 100);
      const taxable = lineSub - lineD;
      const lineTax = taxable * (Number(li.tax_pct || 0) / 100);
      subtotal += lineSub; lineDisc += lineD; taxTotal += lineTax;
    });
    const overallDisc = (subtotal - lineDisc) * (Number(form.discount_pct || 0) / 100);
    const grand = subtotal - lineDisc - overallDisc + taxTotal + Number(form.additional_charges || 0);
    return { subtotal, lineDisc, overallDisc, taxTotal, grand };
  }, [form.items, form.discount_pct, form.additional_charges]);

  const save = async () => {
    if (!form.client_id) { toast.error("Pick a client"); return; }
    if (!form.items.some((i) => i.description?.trim())) { toast.error("Add at least one line"); return; }
    setBusy(true);
    try {
      const payload = {
        client_id: form.client_id,
        issue_date: form.issue_date, currency: form.currency,
        items: form.items.filter((i) => i.description?.trim()).map((i) => ({
          item_id: i.item_id || null, description: i.description,
          qty: Number(i.qty || 0), rate: Number(i.rate || 0),
          discount_pct: Number(i.discount_pct || 0), tax_pct: Number(i.tax_pct || 0),
          unit: i.unit || "unit", hsn_sac: i.hsn_sac || null,
        })),
        discount_pct: Number(form.discount_pct || 0),
        additional_charges: Number(form.additional_charges || 0),
        subject: (form.subject || "").trim() || null,
        notes: form.notes, terms: form.terms,
      };
      if (kind === "quotations") payload.valid_until = form.valid_until || null;
      if (kind === "invoices") payload.due_date = form.due_date || null;
      if (isNew) {
        await api.post(`/workspaces/${activeWorkspaceId}/${meta.endpoint}`, payload);
        toast.success(`${meta.singular} created`);
      } else {
        await api.patch(`/${meta.endpoint}/${doc.id}`, payload);
        toast.success(`${meta.singular} updated`);
      }
      await onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto" data-testid={`${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle>{isNew ? `New ${meta.singular.toLowerCase()}` : `Edit ${doc.doc_no}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-2">
              <Label className="block mb-1.5 text-sm font-semibold">Client *</Label>
              <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
                <SelectTrigger className="h-10" data-testid="doc-client"><SelectValue placeholder="Pick a client…" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Field label="Issue date" type="date" value={form.issue_date} onChange={(v) => set("issue_date", v)} testid="doc-issue" />
            {kind === "quotations" && <Field label="Valid until" type="date" value={form.valid_until} onChange={(v) => set("valid_until", v)} testid="doc-valid" />}
            {kind === "invoices" && <Field label="Due date" type="date" value={form.due_date} onChange={(v) => set("due_date", v)} testid="doc-due" />}
            <Field label="Currency" value={form.currency} onChange={(v) => set("currency", v)} testid="doc-currency" />
          </div>

          <div className="border border-border" data-testid="doc-lines">
            {suggestions.length > 0 && (
              <div className="bg-gradient-to-r from-primary/5 to-primary/10 border-b border-border p-3" data-testid="smart-composer-strip">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] uppercase tracking-widest text-primary font-bold">✨ Frequently added with this client</span>
                  <span className="text-[10px] text-muted-foreground">click to add</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.slice(0, 6).map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => addSuggestion(s)}
                      className="text-left rounded-full border border-primary/30 bg-white hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors px-3 py-1.5 text-xs group"
                      data-testid={`smart-chip-${i}`}
                      title={`Seen ${s.seen_times}× · avg ₹${s.avg_rate}`}
                    >
                      <span className="font-medium">{s.description}</span>
                      <span className="ml-2 text-[10px] font-mono opacity-70 group-hover:opacity-100">₹{Number(s.avg_rate || 0).toLocaleString()} · {s.seen_times}×</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-12 gap-1 px-3 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
              <div className="col-span-4">Description</div>
              <div className="col-span-2">Item ↔</div>
              <div className="col-span-1 text-right">Qty</div>
              <div className="col-span-2 text-right">Rate</div>
              <div className="col-span-1 text-right">Disc %</div>
              <div className="col-span-1 text-right">Tax %</div>
              <div className="col-span-1"></div>
            </div>
            {form.items.map((li, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-1 px-3 py-2 border-b border-border items-center" data-testid={`doc-line-${idx}`}>
                <Input value={li.description} onChange={(e) => setItem(idx, "description", e.target.value)} className="col-span-4 h-9 text-sm" placeholder="Description" data-testid={`line-desc-${idx}`} />
                <Select value={li.item_id || "_none"} onValueChange={(v) => v !== "_none" && pickFromCatalog(idx, v)}>
                  <SelectTrigger className="col-span-2 h-9 text-xs" data-testid={`line-catalog-${idx}`}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">—</SelectItem>
                    {catalog.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.rate}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" value={li.qty} onChange={(e) => setItem(idx, "qty", e.target.value)} className="col-span-1 h-9 text-sm text-right font-mono" data-testid={`line-qty-${idx}`} />
                <Input type="number" value={li.rate} onChange={(e) => setItem(idx, "rate", e.target.value)} className="col-span-2 h-9 text-sm text-right font-mono" data-testid={`line-rate-${idx}`} />
                <Input type="number" value={li.discount_pct} onChange={(e) => setItem(idx, "discount_pct", e.target.value)} className="col-span-1 h-9 text-sm text-right font-mono" data-testid={`line-disc-${idx}`} />
                <Input type="number" value={li.tax_pct} onChange={(e) => setItem(idx, "tax_pct", e.target.value)} className="col-span-1 h-9 text-sm text-right font-mono" data-testid={`line-tax-${idx}`} />
                <Button size="sm" variant="ghost" className="col-span-1 rounded-none h-9 px-2" onClick={() => removeItem(idx)} data-testid={`line-remove-${idx}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
            <div className="p-2">
              <Button size="sm" variant="outline" onClick={addItem} className="rounded-none" data-testid="doc-add-line"><Plus className="w-3.5 h-3.5 mr-1" /> Add line</Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              {/* Feb 2026 · Invoice subject — a one-line human summary
                  surfaced on the invoice list and above line items on
                  the PDF. Rendered on both invoices and quotations
                  for parity. */}
              <div>
                <Label className="block mb-1.5 text-sm font-semibold">
                  Subject
                  <span className="ml-1.5 text-[10px] font-normal text-muted-foreground uppercase tracking-widest">optional</span>
                </Label>
                <Input
                  value={form.subject}
                  onChange={(e) => set("subject", e.target.value)}
                  placeholder={`e.g. "January retainer — content + ads"`}
                  maxLength={200}
                  data-testid="doc-subject"
                />
              </div>
              <div>
                <Label className="block mb-1.5 text-sm font-semibold">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} data-testid="doc-notes" />
              </div>
              <div>
                <Label className="block mb-1.5 text-sm font-semibold">Terms & conditions</Label>
                <Textarea value={form.terms} onChange={(e) => set("terms", e.target.value)} rows={2} data-testid="doc-terms" />
              </div>
            </div>
            <div className="border border-border p-4 space-y-2 self-start" data-testid="doc-totals">
              <Row label="Subtotal" value={totals.subtotal} ccy={form.currency} />
              <Row label="Line discounts" value={-totals.lineDisc} ccy={form.currency} />
              <div className="flex items-center gap-2">
                <Label className="text-xs flex-1">Overall discount %</Label>
                <Input type="number" value={form.discount_pct} onChange={(e) => set("discount_pct", e.target.value)} className="h-8 w-24 text-right font-mono" data-testid="doc-discount-pct" />
              </div>
              <Row label="Overall discount" value={-totals.overallDisc} ccy={form.currency} />
              <Row label="Tax (sum of line taxes)" value={totals.taxTotal} ccy={form.currency} />
              <div className="flex items-center gap-2">
                <Label className="text-xs flex-1">Additional charges</Label>
                <Input type="number" value={form.additional_charges} onChange={(e) => set("additional_charges", e.target.value)} className="h-8 w-24 text-right font-mono" data-testid="doc-additional" />
              </div>
              <div className="flex items-center justify-between border-t border-border pt-2 mt-2">
                <span className="font-bold">Grand total</span>
                <span className="font-mono text-lg font-bold" data-testid="doc-grand-total">{form.currency} {totals.grand.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="doc-save">{busy ? "Saving…" : isNew ? `Create ${meta.singular.toLowerCase()}` : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, ccy }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{ccy} {Number(value).toFixed(2)}</span>
    </div>
  );
}

function DocView({ kind, meta, doc, onClose, onChanged }) {
  const [full, setFull] = useState(doc);
  const [template, setTemplate] = useState(null);
  const [recording, setRecording] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", method: "bank_transfer", reference: "", paid_at: new Date().toISOString().slice(0, 10), notes: "" });

  useEffect(() => {
    if (kind === "invoices") {
      api.get(`/invoices/${doc.id}`).then(({ data }) => setFull(data));
    }
    // Fetch template (fixed schema doc-templates for this kind)
    const tplKind = kind === "quotations" ? "quotation" : kind === "sales-orders" ? "purchase_order" : "invoice";
    api.get(`/workspaces/${doc.workspace_id}/doc-templates`)
      .then(({ data }) => setTemplate(data?.[tplKind] || null))
      .catch(() => setTemplate(null));
  }, [doc.id, doc.workspace_id, kind]);

  const status = full.status;
  const changeStatus = async (newStatus) => {
    try {
      await api.patch(`/${meta.endpoint}/${doc.id}`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      const { data } = await api.get(`/${meta.endpoint === "sales-orders" ? "sales-orders" : meta.endpoint.replace(/s$/, "s")}/${doc.id}`);
      setFull(data);
      onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const convertToSO = async () => {
    if (kind !== "quotations") return;
    try {
      const { data } = await api.post(`/quotations/${doc.id}/convert-to-so`);
      toast.success(`Sales order ${data.doc_no} created`);
      onChanged?.();
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Convert failed")); }
  };

  const convertToInvoice = async () => {
    if (kind !== "sales-orders") return;
    try {
      const { data } = await api.post(`/sales-orders/${doc.id}/convert-to-invoice`);
      toast.success(`Invoice ${data.doc_no} created`);
      onChanged?.();
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Convert failed")); }
  };

  const recordPayment = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) { toast.error("Enter a positive amount"); return; }
    setRecording(true);
    try {
      await api.post(`/workspaces/${doc.workspace_id}/payments`, {
        invoice_id: doc.id,
        amount: Number(payForm.amount),
        method: payForm.method,
        reference: payForm.reference,
        paid_at: payForm.paid_at,
        notes: payForm.notes,
      });
      toast.success("Payment recorded");
      const { data } = await api.get(`/invoices/${doc.id}`);
      setFull(data);
      // Always celebrate a payment received; extra celebration when fully paid.
      celebrate("payment_received", { amount: payForm.amount, doc_no: doc.doc_no });
      if ((data?.balance_due ?? 1) <= 0) celebrate("invoice_paid", { doc_no: doc.doc_no });
      setPayForm({ amount: "", method: "bank_transfer", reference: "", paid_at: new Date().toISOString().slice(0, 10), notes: "" });
      onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Record failed")); }
    finally { setRecording(false); }
  };

  // Iter 158 — Copy doc as image / Send / Track
  const copyDocAsImage = async () => {
    const el = document.querySelector('[data-testid="doc-preview"]');
    if (!el) { toast.error("Preview not ready"); return; }
    try {
      const { toBlob } = await import("html-to-image");
      const blob = await toBlob(el, { pixelRatio: 2, backgroundColor: "#ffffff" });
      if (!blob) { toast.error("Snapshot failed"); return; }
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({ [blob.type]: blob })]);
        toast.success("Copied — paste into WhatsApp/Slack/email");
      } else {
        // Fallback: download.
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${doc.doc_no}.png`; a.click();
        URL.revokeObjectURL(url);
        toast.success("Downloaded (clipboard API unavailable)");
      }
    } catch (e) {
      toast.error("Snapshot failed");
    }
  };

  const [sendOpen, setSendOpen] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const trackingKind = kind === "quotations" ? "quote" : kind === "invoices" ? "invoice" : null;
  useEffect(() => {
    if (!trackingKind) return;
    api.get(`/sales-cc/${trackingKind}/${doc.id}/timeline`)
      .then(({ data }) => setTimeline(data))
      .catch(() => setTimeline(null));
  }, [trackingKind, doc.id]);

  const sendDoc = async (payload) => {
    try {
      const { data } = await api.post(`/sales-cc/${trackingKind}/${doc.id}/send`, payload);
      toast.success("Sent — tracking active");
      // Refresh timeline
      const { data: tl } = await api.get(`/sales-cc/${trackingKind}/${doc.id}/timeline`);
      setTimeline(tl);
      setSendOpen(false);
      onChanged?.();
      return data;
    } catch (e) {
      toast.error(apiErrorMessage(e, "Send failed"));
    }
  };

  const print = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const ccy = full.currency || "INR";
    const linesHtml = (full.items || []).map((li) => `<tr>
      <td>${li.description || ""}</td>
      <td style="text-align:center">${li.qty}</td>
      <td style="text-align:right">${ccy} ${Number(li.rate || 0).toFixed(2)}</td>
      <td style="text-align:right">${li.discount_pct || 0}%</td>
      <td style="text-align:right">${li.tax_pct || 0}%</td>
      <td style="text-align:right;font-weight:bold">${ccy} ${Number(li.line_total || 0).toFixed(2)}</td>
    </tr>`).join("");
    w.document.write(`<!DOCTYPE html><html><head><title>${full.doc_no}</title>
      <style>
        body { font-family: -apple-system, system-ui, sans-serif; padding: 40px; color:#111; }
        h1 { font-size: 32px; margin: 0 0 8px; letter-spacing: -0.02em; }
        .label { color:#666; font-size:11px; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom:4px; }
        table { width:100%; border-collapse: collapse; margin: 24px 0; }
        th, td { padding:10px; border-bottom: 1px solid #ddd; font-size:13px; text-align:left; }
        th { background:#f5f5f5; font-size:11px; text-transform:uppercase; letter-spacing:0.05em; }
        .totals { margin-left: auto; width: 360px; }
        .totals .row { display:flex; justify-content:space-between; padding: 6px 0; }
        .totals .grand { border-top:2px solid #111; font-weight:bold; font-size:18px; margin-top:8px; padding-top:12px; }
        .header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:24px; }
        .footer { margin-top:32px; padding-top:16px; border-top:1px solid #ddd; font-size:12px; color:#666; }
      </style></head><body>
      <div class="header">
        <div>
          <div class="label">${meta.singular}</div>
          <h1>${full.doc_no}</h1>
          <div style="font-size:14px">Status: <strong>${status}</strong></div>
          <div style="font-size:13px">Issue date: ${full.issue_date || "—"}</div>
          ${full.due_date ? `<div style="font-size:13px">Due: ${full.due_date}</div>` : ""}
          ${full.valid_until ? `<div style="font-size:13px">Valid till: ${full.valid_until}</div>` : ""}
        </div>
        <div style="text-align:right">
          <div class="label">Bill to</div>
          <div style="font-size:18px;font-weight:bold">${full.client_name}</div>
        </div>
      </div>
      ${full.subject ? `<div style="background:#f3f4f6;border-left:3px solid #0ea5e9;padding:10px 14px;margin:16px 0;font-size:13px;color:#111827;"><strong>Subject:</strong> ${full.subject}</div>` : ""}
      <table>
        <thead><tr><th>Description</th><th style="text-align:center">Qty</th><th style="text-align:right">Rate</th><th style="text-align:right">Disc</th><th style="text-align:right">Tax</th><th style="text-align:right">Total</th></tr></thead>
        <tbody>${linesHtml}</tbody>
      </table>
      <div class="totals">
        <div class="row"><span>Subtotal</span><span>${ccy} ${Number(full.subtotal || 0).toFixed(2)}</span></div>
        <div class="row"><span>Line discount</span><span>${ccy} ${Number(full.line_discount || 0).toFixed(2)}</span></div>
        <div class="row"><span>Overall discount (${full.discount_pct || 0}%)</span><span>${ccy} ${Number(full.overall_discount || 0).toFixed(2)}</span></div>
        <div class="row"><span>Tax</span><span>${ccy} ${Number(full.tax_total || 0).toFixed(2)}</span></div>
        ${full.additional_charges ? `<div class="row"><span>Additional</span><span>${ccy} ${Number(full.additional_charges).toFixed(2)}</span></div>` : ""}
        <div class="row grand"><span>Grand total</span><span>${ccy} ${Number(full.grand_total || 0).toFixed(2)}</span></div>
        ${kind === "invoices" ? `<div class="row" style="color:#0a0"><span>Paid</span><span>${ccy} ${Number(full.amount_paid || 0).toFixed(2)}</span></div>` : ""}
        ${kind === "invoices" ? `<div class="row" style="font-weight:bold"><span>Balance due</span><span>${ccy} ${Number(full.balance_due || 0).toFixed(2)}</span></div>` : ""}
      </div>
      ${full.notes ? `<div class="footer"><strong>Notes:</strong> ${full.notes}</div>` : ""}
      ${full.terms ? `<div class="footer"><strong>Terms:</strong> ${full.terms}</div>` : ""}
      <script>window.onload = () => window.print();</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto" data-testid={`${kind}-view`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>{full.doc_no}</span>
            <StatusChip status={status} />
            <span className="ml-auto text-sm font-normal">
              <Button size="sm" variant="outline" onClick={copyDocAsImage} className="rounded-none mr-2" data-testid="doc-copy-image" title="Copy the invoice as a beautiful image — paste into WhatsApp/Slack/email">
                <FileImage className="w-3.5 h-3.5 mr-1" /> Copy image
              </Button>
              {trackingKind && (
                <Button size="sm" onClick={() => setSendOpen(true)} className="rounded-none mr-2" data-testid="doc-send-track">
                  <Send className="w-3.5 h-3.5 mr-1" /> Send &amp; Track
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={print} className="rounded-none mr-2" data-testid="doc-print"><Printer className="w-3.5 h-3.5 mr-1" /> Print / PDF</Button>
              {kind === "invoices" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/v1/invoices/${doc.id}/pdf`, "_blank", "noopener,noreferrer")}
                  className="rounded-none mr-2"
                  data-testid="doc-download-pdf"
                  title="Open the branded server-rendered PDF"
                >
                  <Printer className="w-3.5 h-3.5 mr-1" /> Download PDF
                </Button>
              )}
              {kind === "quotations" && status === "draft" && <Button size="sm" onClick={() => changeStatus("sent")} className="rounded-none mr-2" data-testid="doc-send">Mark sent</Button>}
              {kind === "quotations" && (status === "sent" || status === "accepted") && <Button size="sm" onClick={convertToSO} className="rounded-none" data-testid="doc-to-so">→ Sales order</Button>}
              {kind === "sales-orders" && status === "open" && <Button size="sm" onClick={convertToInvoice} className="rounded-none" data-testid="doc-to-invoice">→ Invoice</Button>}
              {kind === "invoices" && status === "draft" && <Button size="sm" onClick={() => changeStatus("sent")} className="rounded-none mr-2" data-testid="doc-send">Mark sent</Button>}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          {/* Real document-shaped preview */}
          <DocumentPreview doc={full} template={template} kind={kind} meta={meta} />

          {/* Tracking timeline strip */}
          {trackingKind && timeline && (timeline.sent_count > 0 || timeline.open_count > 0) && (
            <div className="rounded-md border border-border bg-muted/30 p-3" data-testid="doc-timeline">
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1"><Send className="h-3 w-3" /> Sent <b>{timeline.sent_count}×</b></span>
                <span className="flex items-center gap-1"><Eye className="h-3 w-3 text-blue-600" /> Opened <b>{timeline.open_count}×</b></span>
                {timeline.first_open && <span className="text-muted-foreground">First open: {new Date(timeline.first_open).toLocaleString()}</span>}
                {timeline.is_opened && <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-bold"><CheckCircle2 className="h-3 w-3" />OPENED</span>}
              </div>
            </div>
          )}

          {/* Send modal */}
          <SendDocModal open={sendOpen} onClose={() => setSendOpen(false)} onSend={sendDoc} doc={full} kind={trackingKind} />

          {kind === "invoices" && (
            <InvoicePaySection
              invoice={full}
              onChanged={async () => {
                const { data } = await api.get(`/invoices/${doc.id}`);
                setFull(data);
                onChanged?.();
              }}
            />
          )}
          {kind === "invoices" && full.balance_due > 0 && (
            <div className="border border-border p-4 space-y-3" data-testid="invoice-record-payment">
              <div className="label-eyebrow">Record payment</div>
              <div className="grid grid-cols-5 gap-2">
                <Field label="Amount *" type="number" value={payForm.amount} onChange={(v) => setPayForm({ ...payForm, amount: v })} testid="pay-amount" />
                <div>
                  <Label className="block mb-1.5 text-sm font-semibold">Method</Label>
                  <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                    <SelectTrigger className="h-10" data-testid="pay-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{["bank_transfer", "cash", "cheque", "card", "upi", "other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Field label="Reference" value={payForm.reference} onChange={(v) => setPayForm({ ...payForm, reference: v })} testid="pay-reference" />
                <Field label="Paid on" type="date" value={payForm.paid_at} onChange={(v) => setPayForm({ ...payForm, paid_at: v })} testid="pay-date" />
                <div className="flex items-end">
                  <Button onClick={recordPayment} disabled={recording || !payForm.amount} className="rounded-none w-full" data-testid="pay-submit">{recording ? "Recording…" : "Record"}</Button>
                </div>
              </div>
            </div>
          )}
          {full.payments?.length > 0 && (
            <div className="border border-border" data-testid="invoice-payments-list">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
                <div className="col-span-2">Date</div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-2">Method</div>
                <div className="col-span-3">Reference</div>
                <div className="col-span-3 text-muted-foreground">Notes</div>
              </div>
              {full.payments.map((p) => (
                <div key={p.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border text-xs">
                  <div className="col-span-2">{p.paid_at}</div>
                  <div className="col-span-2 text-right font-mono font-semibold text-emerald-600">{full.currency} {Number(p.amount).toLocaleString()}</div>
                  <div className="col-span-2">{p.method}</div>
                  <div className="col-span-3 font-mono text-[11px]">{p.reference || "—"}</div>
                  <div className="col-span-3 text-muted-foreground truncate">{p.notes || ""}</div>
                </div>
              ))}
            </div>
          )}

          {kind === "invoices" && (
            <div className="mt-6 border-t border-border pt-4" data-testid="invoice-emails-section">
              <div className="label-eyebrow mb-2">Emails</div>
              <EmailsTab entityType="invoice" entityId={full.id} embedded />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className="font-medium">{value || "—"}</div>
    </div>
  );
}

/* ──────────────────────── PAYMENTS LIST ─────────────────────── */

function PaymentsTab() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try { const { data } = await api.get(`/workspaces/${activeWorkspaceId}/payments`); setRows(data || []); }
    finally { setBusy(false); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  const del = async (p) => {
    if (!confirm(`Delete this payment? Invoice balance will be restored.`)) return;
    try { await api.delete(`/payments/${p.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return busy && rows.length === 0 ? <div className="text-sm">Loading…</div>
    : rows.length === 0 ? <EmptyState title="No payments recorded yet" description="Payments are recorded against invoices. Open any invoice with a balance due and use 'Record payment'." icon={Wallet} testid="payments-empty" />
    : (
      <div className="border border-border" data-testid="payments-table">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Invoice</div>
          <div className="col-span-3">Client</div>
          <div className="col-span-2 text-right">Amount</div>
          <div className="col-span-2">Method · ref</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {rows.map((p) => (
          <div key={p.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border items-center text-sm" data-testid={`payment-row-${p.id}`}>
            <div className="col-span-2 text-xs">{p.paid_at}</div>
            <div className="col-span-2 font-mono text-xs">{p.invoice_no}</div>
            <div className="col-span-3 truncate">{p.client_name}</div>
            <div className="col-span-2 text-right font-mono font-semibold text-emerald-600">{p.currency} {Number(p.amount).toLocaleString()}</div>
            <div className="col-span-2 text-xs"><div>{p.method}</div><div className="text-muted-foreground font-mono">{p.reference || ""}</div></div>
            <div className="col-span-1 text-right">
              <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => del(p)} data-testid={`payment-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    );
}

/* ──────────────────────── REPORTS ─────────────────────── */

function ReportsTab() {
  const { activeWorkspaceId } = useAuth();
  const [tab, setTab] = useState("ar-aging");
  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap" data-testid="reports-tabs">
        {[
          { k: "ar-aging", label: "AR aging" },
          { k: "ledger", label: "Customer ledger" },
          { k: "pnl", label: "P&L summary" },
        ].map((t) => (
          <Button key={t.k} size="sm" variant={tab === t.k ? "default" : "outline"} className="rounded-none" onClick={() => setTab(t.k)} data-testid={`reports-tab-${t.k}`}>{t.label}</Button>
        ))}
      </div>
      {tab === "ar-aging" && <ARAgingReport wsId={activeWorkspaceId} />}
      {tab === "ledger" && <CustomerLedgerReport wsId={activeWorkspaceId} />}
      {tab === "pnl" && <PnlReport wsId={activeWorkspaceId} />}
    </div>
  );
}

function ARAgingReport({ wsId }) {
  const [d, setD] = useState(null);
  useEffect(() => { if (!wsId) return; api.get(`/workspaces/${wsId}/reports/ar-aging`).then((r) => setD(r.data)); }, [wsId]);
  if (!d) return <div className="text-sm">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="ar-aging-report">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {Object.entries(d.totals).map(([k, v]) => (
          <div key={k} className="border border-border p-3" data-testid={`ar-bucket-${k}`}>
            <div className="label-eyebrow text-[10px]">{k} days</div>
            <div className="text-xl font-bold font-mono" style={{ fontFamily: "var(--font-heading)" }}>{Number(v).toLocaleString()}</div>
          </div>
        ))}
      </div>
      <div className="border border-border p-4 bg-amber-500/10 flex items-center justify-between" data-testid="ar-grand-total">
        <span className="label-eyebrow">Total outstanding</span>
        <span className="font-mono text-2xl font-bold">{Number(d.grand_total).toLocaleString()}</span>
      </div>
      {Object.entries(d.buckets).filter(([_, list]) => list.length).map(([bucket, list]) => (
        <div key={bucket} className="border border-border">
          <div className="px-4 py-2 border-b border-border bg-muted/40 label-eyebrow">{bucket} days · {list.length}</div>
          {list.map((inv) => (
            <div key={inv.invoice_id} className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border text-sm" data-testid={`ar-inv-${inv.invoice_id}`}>
              <div className="col-span-2 font-mono text-xs">{inv.doc_no}</div>
              <div className="col-span-4 truncate">{inv.client_name}</div>
              <div className="col-span-2 text-xs">{inv.due_date || "—"}</div>
              <div className="col-span-2 text-xs">{inv.days_overdue} d</div>
              <div className="col-span-2 text-right font-mono font-semibold">{inv.currency} {Number(inv.balance_due).toLocaleString()}</div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function CustomerLedgerReport({ wsId }) {
  const [clients, setClients] = useState([]);
  const [cid, setCid] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => { if (!wsId) return; api.get(`/workspaces/${wsId}/clients`).then((r) => { setClients(r.data || []); if (r.data?.[0]) setCid(r.data[0].id); }); }, [wsId]);
  useEffect(() => { if (!cid) return; api.get(`/workspaces/${wsId}/reports/customer-ledger?client_id=${cid}`).then((r) => setD(r.data)); }, [wsId, cid]);
  return (
    <div className="space-y-3" data-testid="customer-ledger-report">
      <Select value={cid} onValueChange={setCid}>
        <SelectTrigger className="w-72" data-testid="ledger-client"><SelectValue /></SelectTrigger>
        <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
      </Select>
      {d && (
        <>
          <div className="border border-border p-4 bg-amber-500/10 flex items-center justify-between" data-testid="ledger-outstanding">
            <span className="label-eyebrow">Outstanding</span>
            <span className="font-mono text-2xl font-bold">{Number(d.outstanding).toLocaleString()}</span>
          </div>
          <div className="border border-border">
            <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border bg-muted/40 label-eyebrow text-[10px]">
              <div className="col-span-2">Date</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-3">Ref</div>
              <div className="col-span-2 text-right">Amount</div>
              <div className="col-span-3 text-right">Running balance</div>
            </div>
            {(d.events || []).map((e, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border text-sm" data-testid={`ledger-event-${i}`}>
                <div className="col-span-2 text-xs">{e.date}</div>
                <div className="col-span-2">{e.kind}</div>
                <div className="col-span-3 font-mono text-xs">{e.ref}{e.invoice_no ? ` → ${e.invoice_no}` : ""}</div>
                <div className={`col-span-2 text-right font-mono ${e.kind === "payment" ? "text-emerald-600" : "text-foreground"}`}>{e.kind === "payment" ? "-" : "+"}{Number(e.amount).toLocaleString()}</div>
                <div className="col-span-3 text-right font-mono font-semibold">{Number(e.running_balance).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function PnlReport({ wsId }) {
  const [d, setD] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  useEffect(() => { if (!wsId) return; api.get(`/workspaces/${wsId}/reports/pnl-summary?year=${year}`).then((r) => setD(r.data)); }, [wsId, year]);
  if (!d) return <div className="text-sm">Loading…</div>;
  const maxRev = Math.max(1, ...d.months.map((m) => Math.max(m.revenue, m.collected)));
  return (
    <div className="space-y-4" data-testid="pnl-report">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-semibold">Year</Label>
        <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-8 w-24 font-mono" data-testid="pnl-year" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="border border-border p-3" data-testid="pnl-revenue-total">
          <div className="label-eyebrow text-[10px]">Total invoiced</div>
          <div className="text-2xl font-bold font-mono" style={{ fontFamily: "var(--font-heading)" }}>{Number(d.totals.revenue).toLocaleString()}</div>
        </div>
        <div className="border border-border p-3" data-testid="pnl-collected-total">
          <div className="label-eyebrow text-[10px]">Total collected</div>
          <div className="text-2xl font-bold font-mono text-emerald-600" style={{ fontFamily: "var(--font-heading)" }}>{Number(d.totals.collected).toLocaleString()}</div>
        </div>
      </div>
      <div className="border border-border">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border bg-muted/40 label-eyebrow text-[10px]">
          <div className="col-span-2">Month</div>
          <div className="col-span-2 text-right">Invoiced</div>
          <div className="col-span-2 text-right">Collected</div>
          <div className="col-span-6">Bar</div>
        </div>
        {d.months.map((m) => (
          <div key={m.month} className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border items-center text-sm" data-testid={`pnl-month-${m.month}`}>
            <div className="col-span-2 font-mono text-xs">{m.month}</div>
            <div className="col-span-2 text-right font-mono">{Number(m.revenue).toLocaleString()}</div>
            <div className="col-span-2 text-right font-mono text-emerald-600">{Number(m.collected).toLocaleString()}</div>
            <div className="col-span-6 flex items-center gap-1">
              <div className="h-2 bg-primary" style={{ width: `${(m.revenue / maxRev) * 100}%` }} title="Invoiced" />
              <div className="h-2 bg-emerald-500" style={{ width: `${(m.collected / maxRev) * 100}%` }} title="Collected" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ──────────────────────── shared atoms ─────────────────────── */

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-10" data-testid={testid} />
    </div>
  );
}

function StatTile({ label, count, active, onClick, testid }) {
  return (
    <button onClick={onClick} className={`border ${active ? "border-primary bg-primary/10" : "border-border"} px-3 py-2 text-left transition-colors hover:bg-muted`} data-testid={testid}>
      <div className="label-eyebrow text-[10px] truncate">{label}</div>
      <div className="text-xl font-bold mt-0.5" style={{ fontFamily: "var(--font-heading)" }}>{count}</div>
    </button>
  );
}

// ─────────── DocumentPreview — renders quote/SO/invoice as real doc ───────────

function DocumentPreview({ doc, template, kind, meta }) {
  const tpl = template || {};
  const accent = tpl.accent_color || "#0ea5e9";
  const fontFamily = tpl.font_family === "Times-Roman" ? "serif"
    : tpl.font_family === "Courier" ? "monospace" : "system-ui";
  const ccy = doc.currency || "INR";
  const sym = ccy === "INR" ? "₹" : ccy === "USD" ? "$" : ccy === "EUR" ? "€" : `${ccy} `;
  const money = (n) => `${sym}${Number(n || 0).toLocaleString("en-IN")}`;
  const label = kind === "quotations" ? "QUOTATION"
    : kind === "sales-orders" ? "SALES ORDER" : "TAX INVOICE";

  return (
    <div className="shadow-lg rounded-lg overflow-hidden border border-border bg-white text-black" style={{ fontFamily }} data-testid="doc-preview">
      {/* Accent bar */}
      <div className="h-2" style={{ background: accent }} />

      {/* Header */}
      <div className="p-6 border-b border-gray-200 flex items-start justify-between gap-4">
        <div>
          {tpl.show_logo !== false && (
            <div className="mb-3 h-10 w-24 bg-gray-100 rounded flex items-center justify-center text-[10px] text-gray-400 font-mono">LOGO</div>
          )}
          <div className="text-lg font-bold">Demo Studio</div>
          <div className="text-xs text-gray-600 mt-0.5">Bengaluru, Karnataka · India</div>
          {tpl.show_gstin !== false && (
            <div className="text-xs text-gray-600 font-mono mt-0.5">GSTIN: 27AAACS0000A1Z0</div>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs font-bold uppercase tracking-widest" style={{ color: accent }}>{label}</div>
          <div className="text-2xl font-black mt-1 font-mono">{doc.doc_no}</div>
          <div className="text-xs text-gray-500 mt-1">Issue: {doc.issue_date}</div>
          {doc.due_date && <div className="text-xs text-gray-500">Due: {doc.due_date}</div>}
          {doc.valid_until && <div className="text-xs text-gray-500">Valid till: {doc.valid_until}</div>}
          <div className="mt-2 inline-block px-2 py-0.5 text-[10px] font-bold text-white uppercase" style={{ background: accent }}>{doc.status}</div>
        </div>
      </div>

      {/* Bill to */}
      <div className="grid grid-cols-2 gap-4 p-6 border-b border-gray-200">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Bill to</div>
          <div className="text-sm font-bold">{doc.client_name}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Currency</div>
          <div className="text-sm font-mono">{ccy}</div>
        </div>
      </div>

      {/* Line items */}
      <table className="w-full text-sm" data-testid="doc-preview-lines">
        <thead>
          <tr className="border-b-2" style={{ borderColor: accent }}>
            <th className="text-left p-3 text-[10px] uppercase tracking-widest text-gray-500 font-bold w-8">#</th>
            <th className="text-left p-3 text-[10px] uppercase tracking-widest text-gray-500 font-bold">Description</th>
            <th className="text-center p-3 text-[10px] uppercase tracking-widest text-gray-500 font-bold w-16">HSN</th>
            <th className="text-right p-3 text-[10px] uppercase tracking-widest text-gray-500 font-bold w-14">Qty</th>
            <th className="text-right p-3 text-[10px] uppercase tracking-widest text-gray-500 font-bold w-24">Rate</th>
            <th className="text-right p-3 text-[10px] uppercase tracking-widest text-gray-500 font-bold w-14">Tax</th>
            <th className="text-right p-3 text-[10px] uppercase tracking-widest text-gray-500 font-bold w-28">Amount</th>
          </tr>
        </thead>
        <tbody>
          {(doc.items || []).map((li, idx) => (
            <tr key={idx} className="border-b border-gray-100" data-testid={`doc-preview-line-${idx}`}>
              <td className="p-3 text-gray-500 font-mono">{idx + 1}</td>
              <td className="p-3">{li.description}</td>
              <td className="p-3 text-center text-xs font-mono text-gray-500">{li.hsn_sac || "—"}</td>
              <td className="p-3 text-right font-mono">{li.qty}</td>
              <td className="p-3 text-right font-mono">{money(li.rate)}</td>
              <td className="p-3 text-right text-xs text-gray-600">{li.tax_pct}%</td>
              <td className="p-3 text-right font-mono font-semibold">{money(li.line_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-end p-6 border-t border-gray-200">
        <div className="w-72 space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-mono">{money(doc.subtotal)}</span></div>
          {((doc.line_discount || 0) + (doc.overall_discount || 0)) > 0 && (
            <div className="flex justify-between text-red-600"><span>Discounts</span><span className="font-mono">−{money((doc.line_discount || 0) + (doc.overall_discount || 0))}</span></div>
          )}
          <div className="flex justify-between"><span className="text-gray-600">Tax (GST)</span><span className="font-mono">{money(doc.tax_total)}</span></div>
          <div className="flex justify-between pt-2 mt-2 border-t-2 font-black text-lg" style={{ borderColor: accent }}>
            <span>Grand Total</span>
            <span className="font-mono">{money(doc.grand_total)}</span>
          </div>
          {kind === "invoices" && (
            <>
              <div className="flex justify-between text-sm text-emerald-700 pt-1"><span>Amount paid</span><span className="font-mono">{money(doc.amount_paid)}</span></div>
              <div className="flex justify-between text-sm font-bold text-amber-700"><span>Balance due</span><span className="font-mono" data-testid="invoice-balance">{money(doc.balance_due)}</span></div>
            </>
          )}
        </div>
      </div>

      {/* Terms */}
      {(doc.terms || tpl.terms_html) && (
        <div className="p-6 border-t border-gray-200">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Terms &amp; conditions</div>
          <div className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">{doc.terms || tpl.terms_html}</div>
        </div>
      )}
      {doc.notes && (
        <div className="p-6 border-t border-gray-200">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Notes</div>
          <div className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">{doc.notes}</div>
        </div>
      )}

      {/* Signature + footer */}
      {tpl.show_signature !== false && (
        <div className="flex justify-end px-6 pb-2 text-xs text-gray-500">
          <div className="w-40 border-t border-gray-400 pt-1 text-center">Authorised Signatory</div>
        </div>
      )}
      <div className="bg-gray-50 p-3 text-center text-[10px] text-gray-500 border-t border-gray-200">
        {tpl.footer_html || "Thank you for your business · Powered by Acquilix"}
      </div>
    </div>
  );
}


// ─────────── SendDocModal — Iter 158 · Quote/Invoice Send + Track ───────────

function SendDocModal({ open, onClose, onSend, doc, kind }) {
  const [emails, setEmails] = useState("");
  const [whatsappTo, setWhatsappTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [channel, setChannel] = useState("email");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Pre-fill sensible defaults.
    const labelKind = kind === "quote" ? "quotation" : "invoice";
    setSubject(`${labelKind === "invoice" ? "Invoice" : "Quotation"} ${doc.doc_no} for ${doc.client_name || "you"}`);
    // Feb 2026 · Inline the doc subject into the body so quotation
    // emails mirror the invoice look. Rendered as a highlighted strip
    // right under the greeting; skipped silently when no subject.
    const _subj = (doc.subject || "").trim();
    const subjectStrip = _subj
      ? `<p style="background:#f3f4f6;border-left:3px solid #0ea5e9;padding:8px 12px;color:#111827;font-size:14px;margin:12px 0;"><b>Subject:</b> ${_subj}</p>`
      : "";
    setBody(
      `Hi ${doc.client_name || "there"},\n\n${subjectStrip}Please find attached ${labelKind} ${doc.doc_no} for ${doc.currency || "INR"} ${Number(doc.grand_total || 0).toLocaleString("en-IN")}.\n\n${labelKind === "invoice" ? "Payment is appreciated by the due date." : "Looking forward to your feedback."}\n\nBest regards,`
    );
  }, [open, doc, kind]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await onSend({
        to_emails: emails.split(",").map((s) => s.trim()).filter(Boolean),
        whatsapp_to: whatsappTo || undefined,
        subject, body_html: body, channel,
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="send-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full shadow-2xl overflow-hidden">
        <div className="p-4 border-b border-border flex items-center gap-2">
          <Send className="h-4 w-4" />
          <h3 className="font-bold">Send &amp; Track · {doc.doc_no}</h3>
          <button onClick={onClose} className="ml-auto text-xs border rounded px-2 py-1 hover:bg-muted">Cancel</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <Label className="text-xs">Channel</Label>
            <div className="flex gap-2 mt-1">
              {["email", "whatsapp", "both"].map((c) => (
                <button key={c} type="button" onClick={() => setChannel(c)}
                  className={`px-3 py-1.5 text-xs rounded-full border ${channel === c ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                  data-testid={`send-channel-${c}`}>
                  {c === "email" ? "📧 Email" : c === "whatsapp" ? "💬 WhatsApp" : "Both"}
                </button>
              ))}
            </div>
          </div>
          {(channel === "email" || channel === "both") && (
            <div>
              <Label className="text-xs">To emails (comma separated)</Label>
              <Input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="ap@client.com, cfo@client.com" data-testid="send-emails" />
            </div>
          )}
          {(channel === "whatsapp" || channel === "both") && (
            <div>
              <Label className="text-xs">WhatsApp number (with country code)</Label>
              <Input value={whatsappTo} onChange={(e) => setWhatsappTo(e.target.value)} placeholder="+919999999999" data-testid="send-whatsapp" />
            </div>
          )}
          <div>
            <Label className="text-xs">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="send-subject" />
          </div>
          <div>
            <Label className="text-xs">Body</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={7} data-testid="send-body" />
            <p className="text-[10px] text-muted-foreground mt-1">✨ Includes an invisible tracking pixel to record opens.</p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-none" data-testid="send-submit">
              <Send className="h-3.5 w-3.5 mr-1" />
              {busy ? "Sending…" : "Send & start tracking"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

